
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { 
  MapPin,
  Plus,
  ExternalLink,
  Users,
  TrendingUp,
  Calendar,
  CheckCircle,
  Clock,
  AlertTriangle
} from 'lucide-react';

const HQStudios = () => {
  const studios = [
    {
      id: 1,
      name: "Austin Studio",
      address: "123 Main St, Austin, TX",
      status: "active",
      staff: 8,
      revenue: 45000,
      patients: 156,
      launchDate: "2023-01-15"
    },
    {
      id: 2,
      name: "Dallas Downtown",
      address: "456 Elm St, Dallas, TX",
      status: "active",
      staff: 12,
      revenue: 62000,
      patients: 203,
      launchDate: "2023-03-22"
    },
    {
      id: 3,
      name: "Houston Medical",
      address: "789 Oak Ave, Houston, TX",
      status: "launching",
      staff: 6,
      revenue: 0,
      patients: 0,
      launchDate: "2024-02-01"
    }
  ];

  const launchSteps = [
    { title: "Site Preparation", completed: true },
    { title: "Staff Hiring", completed: true },
    { title: "Equipment Installation", completed: true },
    { title: "Staff Training", completed: false },
    { title: "Marketing Launch", completed: false },
    { title: "Grand Opening", completed: false }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-amber-900">
      {/* Header */}
      <div className="bg-slate-800/50 backdrop-blur border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-white">
                Studio Management
              </h1>
              <p className="mt-1 text-amber-200">
                Launch kits, onboarding, and studio oversight
              </p>
            </div>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white">
              <Plus className="w-4 h-4 mr-2" />
              Launch New Studio
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Active Studios Grid */}
          <div>
            <h2 className="text-2xl font-bold text-white mb-6">Active Studios</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {studios.map((studio) => (
                <Card key={studio.id} className="bg-slate-800/50 backdrop-blur border-slate-600/30">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-white">{studio.name}</CardTitle>
                        <p className="text-slate-300 text-sm mt-1">{studio.address}</p>
                      </div>
                      <Badge 
                        className={
                          studio.status === 'active' 
                            ? 'bg-emerald-500/20 text-emerald-200 border-emerald-500/30'
                            : 'bg-amber-500/20 text-amber-200 border-amber-500/30'
                        }
                      >
                        {studio.status === 'active' ? (
                          <CheckCircle className="w-3 h-3 mr-1" />
                        ) : (
                          <Clock className="w-3 h-3 mr-1" />
                        )}
                        {studio.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-slate-400">Staff</p>
                          <p className="text-white font-semibold">{studio.staff}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Patients</p>
                          <p className="text-white font-semibold">{studio.patients}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-slate-400">Monthly Revenue</p>
                          <p className="text-white font-semibold">
                            ${studio.revenue.toLocaleString()}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button asChild size="sm" className="flex-1 bg-slate-700 hover:bg-slate-600 text-white">
                          <Link to={`/hq/checklists`}>
                            <ExternalLink className="w-4 h-4 mr-1" />
                            Launch Kit
                          </Link>
                        </Button>
                        <Button size="sm" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700">
                          Manage
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Launch Pipeline */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
              <CardHeader>
                <CardTitle className="text-white">Launch Pipeline</CardTitle>
                <p className="text-slate-300 text-sm">Houston Medical Center progress</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {launchSteps.map((step, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      {step.completed ? (
                        <CheckCircle className="w-5 h-5 text-emerald-400" />
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-slate-500"></div>
                      )}
                      <span className={`${
                        step.completed ? 'text-white' : 'text-slate-400'
                      }`}>
                        {step.title}
                      </span>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 pt-4 border-t border-slate-600">
                  <Button asChild className="w-full bg-amber-600 hover:bg-amber-700">
                    <Link to="/hq/checklists">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Open Launch Kit
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Quick Resources */}
            <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
              <CardHeader>
                <CardTitle className="text-white">Quick Resources</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <Button asChild variant="outline" className="w-full justify-start border-slate-600 text-slate-300 hover:bg-slate-700">
                    <Link to="/hq/kits">
                      <Users className="w-4 h-4 mr-2" />
                      Studio Onboarding Hub
                    </Link>
                  </Button>
                  
                  <Button asChild variant="outline" className="w-full justify-start border-slate-600 text-slate-300 hover:bg-slate-700">
                    <Link to="/hq/sops">
                      <ExternalLink className="w-4 w-4 mr-2" />
                      Studio Setup SOPs
                    </Link>
                  </Button>
                  
                  <Button asChild variant="outline" className="w-full justify-start border-slate-600 text-slate-300 hover:bg-slate-700">
                    <Link to="/hq/training">
                      <Calendar className="w-4 h-4 mr-2" />
                      Staff Training Portal
                    </Link>
                  </Button>
                  
                  <Button asChild variant="outline" className="w-full justify-start border-slate-600 text-slate-300 hover:bg-slate-700">
                    <Link to="/hq/metrics">
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Performance Analytics
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* System Alerts */}
          <Card className="bg-slate-800/50 backdrop-blur border-amber-500/30">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2 text-amber-400" />
                System Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                  <div>
                    <p className="text-amber-200 font-medium">Houston: Staff training completion required</p>
                    <p className="text-amber-300/70 text-sm">3 staff members need to complete onboarding modules</p>
                  </div>
                  <Button size="sm" className="bg-amber-600 hover:bg-amber-700">
                    Review
                  </Button>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                  <div>
                    <p className="text-blue-200 font-medium">Austin: Equipment maintenance scheduled</p>
                    <p className="text-blue-300/70 text-sm">Quarterly maintenance window: Feb 15-16</p>
                  </div>
                  <Button size="sm" variant="outline" className="border-blue-500/30 text-blue-200">
                    Schedule
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default HQStudios;
